import { useCallback, useEffect, useState } from 'react';
import { PlatformApiError, PlatformUnreachableError, type Viewer } from '@sfc/sdk';
import { platform } from '../api/client';

const REVALIDATE_INTERVAL_MS = 15_000;

export type ViewerState =
  | { status: 'checking' }
  | { status: 'signed-out' }
  | { status: 'signed-in'; viewer: Viewer }
  | { status: 'unreachable' };

/**
 * Who, if anyone, is signed in.
 *
 * The answer always comes from the server. Nothing about the session is kept in
 * this browser, because the session cookie is http-only and a copy here would
 * only ever be a second, staler opinion: it would keep showing a dashboard to
 * someone whose session had already been revoked.
 */
export function useViewer(): {
  state: ViewerState;
  signIn: (email: string, password: string) => Promise<void>;
  signOut: () => Promise<void>;
  refresh: () => void;
} {
  const [state, setState] = useState<ViewerState>({ status: 'checking' });

  const refresh = useCallback(async (signal?: AbortSignal) => {
    try {
      const viewer = await platform.auth.currentViewer(signal);
      setState(viewer ? { status: 'signed-in', viewer } : { status: 'signed-out' });
    } catch (error) {
      if (signal?.aborted) return;
      if (error instanceof PlatformUnreachableError) {
        setState({ status: 'unreachable' });
        return;
      }
      setState({ status: 'signed-out' });
    }
  }, []);

  useEffect(() => {
    const controller = new AbortController();
    void refresh(controller.signal);

    // A role change or a revoked membership takes effect on the server at once,
    // and this is what makes an open dashboard notice. Without it, somebody
    // demoted while looking at the screen would keep seeing navigation they can
    // no longer use until they happened to reload.
    const timer = setInterval(() => void refresh(controller.signal), REVALIDATE_INTERVAL_MS);
    const onFocus = (): void => void refresh(controller.signal);
    window.addEventListener('focus', onFocus);

    return () => {
      controller.abort();
      clearInterval(timer);
      window.removeEventListener('focus', onFocus);
    };
  }, [refresh]);

  const signIn = useCallback(async (email: string, password: string) => {
    const viewer = await platform.auth.login({ email, password });
    setState({ status: 'signed-in', viewer });
  }, []);

  const signOut = useCallback(async () => {
    try {
      await platform.auth.logout();
    } finally {
      // Whatever the server said, this browser stops showing a dashboard. A
      // failed sign-out that left the screen up would be the worse outcome.
      setState({ status: 'signed-out' });
    }
  }, []);

  return { state, signIn, signOut, refresh: () => void refresh() };
}

/** Turns a failure into something worth reading, without leaking specifics. */
export function describeSignInFailure(error: unknown): string {
  if (error instanceof PlatformUnreachableError) return 'The API did not answer. Is it running?';
  if (error instanceof PlatformApiError) {
    if (error.status === 429) return 'Too many attempts. Wait a little and try again.';
    if (error.status === 401) return 'Those sign-in details are not right.';
    if (error.status === 403) return 'This dashboard is not allowed to sign in against that API.';
  }
  return 'Something went wrong signing in.';
}
